import React from "react";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, IconButton, MenuItem, TextField, Typography } from "@mui/material";
import { X, Move, RotateCw, Blend, ALargeSmall, Contrast } from "lucide-react";
import {
    renderWatermark,
    WATERMARK_PLACEHOLDERS,
    WATERMARK_POSITION_OPTIONS,
    WATERMARK_ROTATION_OPTIONS,
    WATERMARK_OPACITY_OPTIONS,
    WATERMARK_FONT_SIZE_OPTIONS,
    WatermarkOptions,
    WatermarkPosition,
} from "../../util/watermark";
import ColorSwatchInput from "../ui/color-swatch-input";

const TEAL = 'var(--color-primary)';
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

// Sample values for the preview only; real viewers get their own email and IP.
const SAMPLE = { email: 'jane.doe@company.com', ip: '203.0.113.10' };

// The preview page is drawn at PREVIEW_SCALE of a real A4 page (595pt wide),
// so font size and the edge margin look the same relative to the page as
// stampPdf (document-service.js) actually stamps them.
const PAGE_WIDTH_PT = 595;
const PREVIEW_PAGE_WIDTH = 300;
const PREVIEW_SCALE = PREVIEW_PAGE_WIDTH / PAGE_WIDTH_PT;
const MARGIN_PT = 24; // stampPdf's WATERMARK_MARGIN

const hexToRgba = (hex: string, alpha: number): string => {
    const r = parseInt(hex.slice(1, 3), 16) || 0;
    const g = parseInt(hex.slice(3, 5), 16) || 0;
    const b = parseInt(hex.slice(5, 7), 16) || 0;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

type Props = {
    open: boolean;
    template: string;
    options: WatermarkOptions;
    onApply: (template: string, options: WatermarkOptions) => void;
    onClose: () => void;
};

// One settings row: icon + label on the left, its select on the right.
const SettingRow: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({ icon, label, children }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: '130px 1fr', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'var(--color-default-text)' }}>
            {icon}
            <Typography sx={{ fontSize: 13 }}>{label}</Typography>
        </Box>
        {children}
    </Box>
);

const selectSx = { '& .MuiInputBase-root': { fontSize: 13, borderRadius: 1.5 } };
const menuItemSx = { fontSize: 13 };

// Presets for a select, plus the current value as "Custom" when it isn't one
// of them (e.g. saved before the presets existed), so it still displays.
const withCurrent = (options: { value: number; label: string }[], current: number, customLabel: (v: number) => string) =>
    options.some(o => o.value === current) ? options : [...options, { value: current, label: customLabel(current) }];

// Edit the watermark text/look and see it on a sample page. Placeholders are inserted by
// clicking them, so the text stays valid. The preview approximates stampPdf's actual
// rendering (same rotation/opacity/fontSize/color/position) with CSS - close enough to
// judge the look without round-tripping through the server.
const WatermarkPreview: React.FC<Props> = ({ open, template, options, onApply, onClose }) => {
    const [draft, setDraft] = React.useState(template);
    const [draftOptions, setDraftOptions] = React.useState(options);
    const [colorInput, setColorInput] = React.useState(options.fontColor);

    React.useEffect(() => {
        if (open) { setDraft(template); setDraftOptions(options); setColorInput(options.fontColor); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, template]);

    const set = (patch: Partial<WatermarkOptions>) => setDraftOptions(prev => ({ ...prev, ...patch }));

    const previewText = renderWatermark(draft, SAMPLE);
    const previewColor = hexToRgba(draftOptions.fontColor, draftOptions.opacity);
    const previewFontSize = Math.max(4, draftOptions.fontSize * PREVIEW_SCALE);
    const previewMargin = MARGIN_PT * PREVIEW_SCALE;

    const textSx = {
        fontFamily: 'Helvetica, Arial, sans-serif',
        fontSize: previewFontSize,
        lineHeight: 1,
        color: previewColor,
        whiteSpace: 'nowrap' as const,
        userSelect: 'none' as const,
        transform: `rotate(${-draftOptions.rotation}deg)`,
    };

    // Single-placement positions: "<row>-<column>", 'center' = middle-center.
    const renderPlaced = () => {
        const [row, column] = draftOptions.position === 'center' ? ['middle', 'center'] : draftOptions.position.split('-');
        return (
            <Box sx={{
                position: 'absolute',
                inset: previewMargin,
                display: 'flex',
                justifyContent: column === 'left' ? 'flex-start' : column === 'right' ? 'flex-end' : 'center',
                alignItems: row === 'top' ? 'flex-start' : row === 'bottom' ? 'flex-end' : 'center',
            }}>
                <Typography component="span" sx={{ ...textSx, transformOrigin: column === 'left' ? 'left bottom' : column === 'right' ? 'right bottom' : 'center' }}>
                    {previewText}
                </Typography>
            </Box>
        );
    };

    const rotationOptions = withCurrent(WATERMARK_ROTATION_OPTIONS, draftOptions.rotation, v => `Custom (${v}°)`);
    const opacityOptions = withCurrent(WATERMARK_OPACITY_OPTIONS, draftOptions.opacity, v => `Custom (${Math.round((1 - v) * 100)}% transparent)`);
    const fontSizeOptions = withCurrent(WATERMARK_FONT_SIZE_OPTIONS.map(v => ({ value: v, label: String(v) })), draftOptions.fontSize, v => String(v));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 2.5, pb: 1.5 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 700 }}>Customize watermark settings</Typography>
                <IconButton size="small" onClick={onClose} aria-label="Close"><X size={18} /></IconButton>
            </Box>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0 }}>
                {/* Sample page, A4 proportions, with the watermark drawn on it. */}
                <Box sx={{ background: '#e9ebee', borderRadius: 1, display: 'flex', justifyContent: 'center', py: 2, overflow: 'hidden' }}>
                    <Box sx={{ position: 'relative', width: PREVIEW_PAGE_WIDTH, height: PREVIEW_PAGE_WIDTH * 0.62, background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.12)', overflow: 'hidden' }}>
                        <Box sx={{ height: 28, background: TEAL, display: 'flex', alignItems: 'center', px: 1.5 }}>
                            <Box sx={{ width: 48, height: 6, borderRadius: 1, background: 'rgba(255,255,255,0.85)' }} />
                        </Box>
                        <Box sx={{ px: 3, pt: 2 }}>
                            <Box sx={{ height: 9, width: '70%', mx: 'auto', borderRadius: 1, background: '#2d3748', mb: 1 }} />
                            <Box sx={{ height: 5, width: '50%', mx: 'auto', borderRadius: 1, background: '#cbd2d9', mb: 2 }} />
                            {[100, 92, 96, 60].map((w, i) => (
                                <Box key={i} sx={{ height: 5, width: `${w}%`, borderRadius: 1, background: '#eef0f2', mb: 0.9 }} />
                            ))}
                        </Box>
                        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
                            {draftOptions.position === 'tiled' ? (
                                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexWrap: 'wrap', alignContent: 'space-around', justifyContent: 'space-around' }}>
                                    {Array.from({ length: 8 }).map((_, i) => (
                                        <Typography key={i} component="span" sx={{ ...textSx, px: 1, py: 1.5 }}>{previewText}</Typography>
                                    ))}
                                </Box>
                            ) : renderPlaced()}
                        </Box>
                    </Box>
                </Box>

                <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>Text</Typography>
                    <TextField
                        fullWidth
                        size="small"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        inputProps={{ maxLength: 500 }}
                        sx={selectSx}
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.25 }}>
                        {WATERMARK_PLACEHOLDERS.map(p => (
                            <Chip
                                key={p}
                                label={p}
                                size="small"
                                onClick={() => setDraft(d => `${d}${d && !d.endsWith(' ') ? ' ' : ''}${p}`)}
                                sx={{ fontSize: 12, background: 'var(--color-container-background)', '&:hover': { background: 'rgba(var(--color-primary-rgb), 0.12)' } }}
                            />
                        ))}
                    </Box>
                    <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)', mt: 1 }}>
                        {'{{email}}'} is the viewer's email once they verify, or "Anonymous" on public links. {'{{ip-address}}'} is the viewer's IP.
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <SettingRow icon={<Move size={16} />} label="Position">
                        <TextField select fullWidth size="small" value={draftOptions.position} onChange={(e) => set({ position: e.target.value as WatermarkPosition })} sx={selectSx}>
                            {WATERMARK_POSITION_OPTIONS.map(o => <MenuItem key={o.value} value={o.value} sx={menuItemSx}>{o.label}</MenuItem>)}
                        </TextField>
                    </SettingRow>
                    <SettingRow icon={<RotateCw size={16} />} label="Rotation">
                        <TextField select fullWidth size="small" value={draftOptions.rotation} onChange={(e) => set({ rotation: Number(e.target.value) })} sx={selectSx}>
                            {rotationOptions.map(o => <MenuItem key={o.value} value={o.value} sx={menuItemSx}>{o.label}</MenuItem>)}
                        </TextField>
                    </SettingRow>
                    <SettingRow icon={<Blend size={16} />} label="Opacity">
                        <TextField select fullWidth size="small" value={draftOptions.opacity} onChange={(e) => set({ opacity: Number(e.target.value) })} sx={selectSx}>
                            {opacityOptions.map(o => <MenuItem key={o.value} value={o.value} sx={menuItemSx}>{o.label}</MenuItem>)}
                        </TextField>
                    </SettingRow>
                    <SettingRow icon={<ALargeSmall size={16} />} label="Font size">
                        <TextField select fullWidth size="small" value={draftOptions.fontSize} onChange={(e) => set({ fontSize: Number(e.target.value) })} sx={selectSx}>
                            {fontSizeOptions.map(o => <MenuItem key={o.value} value={o.value} sx={menuItemSx}>{o.label}</MenuItem>)}
                        </TextField>
                    </SettingRow>
                    <SettingRow icon={<Contrast size={16} />} label="Font color">
                        {/* Swatch (opens the native color picker) BESIDE the hex
                            field, not inside it - as an adornment, MUI's InputBase
                            kept refocusing its own input on every click while the
                            picker held focus, and that focus/blur ping-pong of
                            state updates hit React's "Maximum update depth exceeded".
                            Typing a full #rrggbb in the field applies it too. */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ColorSwatchInput
                                value={draftOptions.fontColor}
                                onChange={(color) => { set({ fontColor: color }); setColorInput(color); }}
                                ariaLabel="Pick font color"
                            />
                            <TextField
                                fullWidth
                                size="small"
                                value={colorInput}
                                onChange={(e) => {
                                    const v = e.target.value.trim();
                                    setColorInput(v);
                                    if (HEX_RE.test(v)) set({ fontColor: v.toLowerCase() });
                                }}
                                error={!HEX_RE.test(colorInput)}
                                inputProps={{ maxLength: 7 }}
                                sx={selectSx}
                            />
                        </Box>
                    </SettingRow>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'space-between' }}>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{ textTransform: 'uppercase', fontSize: 11, fontWeight: 600, width: 122, height: 34, borderRadius: '4px', borderColor: '#bfc5cc', color: '#444' }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={() => onApply(draft, draftOptions)}
                    sx={{ textTransform: 'uppercase', fontSize: 11, fontWeight: 600, width: 122, height: 34, borderRadius: '4px', boxShadow: 'none', background: TEAL, '&:hover': { background: 'var(--color-primary-hover)', boxShadow: 'none' } }}
                >
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default WatermarkPreview;
